import * as React from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Info } from "lucide-react";
import { cn } from "@/utils/utils";

export interface TInfoTooltipProps {
    /** Tooltip body — plain text or richer JSX (e.g. a token list) */
    content: React.ReactNode;
    /** Accessible label for the default ⓘ trigger. Ignored when `children` is
     *  given: a custom trigger owns its own label. */
    label?: string;
    className?: string;
    /** Custom trigger in place of the ⓘ icon, rendered via Radix `asChild`. Must
     *  be a single DOM element that forwards refs and spreads props, and must
     *  carry its own accessible label and be focusable. */
    children?: React.ReactNode;
    side?: "top" | "right" | "bottom" | "left";
}

/**
 * Reveals explanatory content on hover or keyboard focus, from a small ⓘ icon by
 * default or from whatever `children` provides.
 */
export function InfoTooltip({
    content,
    label = "More information",
    className,
    children,
    side = "top",
}: TInfoTooltipProps) {
    return (
        <Tooltip.Provider delayDuration={150}>
            <Tooltip.Root>
                <Tooltip.Trigger asChild>
                    {children ?? (
                        <button
                            type="button"
                            aria-label={label}
                            className={cn(
                                "inline-flex items-center align-middle text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded-full",
                                className,
                            )}
                        >
                            <Info className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                    )}
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content
                        side={side}
                        align="start"
                        sideOffset={6}
                        className="z-50 max-w-sm rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
                    >
                        {content}
                        <Tooltip.Arrow className="fill-popover" />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </Tooltip.Provider>
    );
}
